import { useTranslation } from 'react-i18next'
import type { InfoGroup } from '#components/tabularOverlay/InfoEntry'
import { useLiveSessionExpiry, useUserData } from '#features/User/userManager'
import { formatDateTime } from '#utils/formatDateTime'
import { formatDuration } from '#utils/formatDuration'
import { tryStringify } from './utils'

export const useSystemInformation = (): InfoGroup[] => {
  const { t, i18n } = useTranslation('shared')

  const systemInfo: InfoGroup = {
    groupId: 'system',
    title: t('system-information.groups.system.title', 'System'),
    entries: [
      {
        entryId: 'time',
        title: t('system-information.groups.system.entries.time-stamp', 'Timestamp'),
        value: formatDateTime(i18n, new Date()),
      },
      {
        entryId: 'browser-locale',
        title: t('system-information.groups.system.entries.browser-locale', 'Browser Locale'),
        value: navigator.languages.join(', '),
      },
      {
        entryId: 'user-locale',
        title: t('system-information.groups.system.entries.user-locale', 'User Locale'),
        value: i18n.language,
      },
      {
        entryId: 'browser-name',
        title: t('system-information.groups.system.entries.browser-name', 'Browser Name'),
        value: navigator.userAgent,
      },
    ],
  }

  const user = useUserData()
  const expiresIn = useLiveSessionExpiry(user)

  const userInfo: InfoGroup = {
    groupId: 'user',
    title: t('system-information.groups.user.title', 'User'),
    entries: [
      {
        entryId: 'user-name',
        title: t('system-information.groups.user.entries.user-name', 'User Name'),
        value: user?.profile ? [user.profile.name, user.profile.given_name, user.profile.family_name].filter(Boolean).join(' ') : undefined,
      },
      {
        entryId: 'man-id',
        title: t('system-information.groups.user.entries.man-id', 'MAN ID'),
        value: tryStringify(user?.profile?.['custom:MANId']),
      },
      {
        entryId: 'cognito-username',
        title: t('system-information.groups.user.entries.cognito-username', 'Cognito Username'),
        value: tryStringify(user?.profile?.['cognito:username']),
      },
      {
        entryId: 'user-id',
        title: t('system-information.groups.user.entries.user-id', 'User ID'),
        value: user?.profile?.sub,
      },
      {
        entryId: 'cognito:groups',
        title: t('system-information.groups.user.entries.cognito-groups', 'Cognito Groups'),
        value: tryStringify(user?.profile?.['cognito:groups']),
      },
      {
        entryId: 'remaining-session-time',
        title: t('system-information.groups.user.entries.remaining-session-time', 'Remaining Session Time'),
        value: expiresIn ? formatDuration(expiresIn * 1000, undefined, { relative: false }) : undefined,
      },
    ],
  }

  const appInfo: InfoGroup = {
    groupId: 'app',
    title: t('system-information.groups.app.title', 'Application'),
    entries: [
      {
        entryId: 'app-environment',
        title: t('system-information.groups.app.entries.environment', 'Environment'),
        value: location.hostname,
      },
      {
        entryId: 'app-version',
        title: t('system-information.groups.app.entries.version', 'Version'),
        value: VITE_APP_VERSION,
      },
      {
        entryId: 'app-build',
        title: t('system-information.groups.app.entries.build', 'Build'),
        value: VITE_COMMIT_SHA,
      },
      {
        entryId: 'app-build-time',
        title: t('system-information.groups.app.entries.build-time', 'Build Time'),
        value: formatDateTime(i18n, new Date(VITE_BUILD_TIME)),
      },
    ],
  }

  return [systemInfo, userInfo, appInfo]
}
